import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom-v5-compat";

import {
  LinkButton,
  Price,
  PrimaryAction,
  TitleBar,
  Button,
} from "../../Atoms";
import { useVenue } from "../../VenueContext";
import { getItemsForSearchTerm } from "../../menuSelectors";
import { useFetchLiveItemCountdown, useFetchMenu } from "../../queries";
import type { Item, ItemCount, Menu } from "../../schema";
import { getVenueHasMobileOrdering } from "../../venueSelectors";

export interface SectionProps {
  menu: Menu;
}
interface ItemBoxProps {
  item: Item;
}

export const ItemCountdown = ({ count }: { count?: number }) => {
  const { t } = useTranslation("onsite");
  if (count == undefined) {
    return null;
  }

  if (count <= 0) {
    return (
      <div className="rounded-venue-brand bg-venue-highlight px-8 py-4 text-xs font-semibold text-venue-tertiary">
        <div>{t("items.soldOut")}</div>
      </div>
    );
  }
  return (
    <div className="rounded-venue-brand bg-venue-highlight px-8 py-4 text-xs font-semibold text-venue-tertiary">
      <div>
        {count <= 0
          ? t("items.soldOut")
          : t("items.countRemaining", {
              count,
            })}
      </div>
    </div>
  );
};

// Because ItemBox is used in the Hubs Item Form, we can not the fetchLiveItemCountdown hook wont work correctly
// So we seperate the data retrieval from the component
export const ItemBoxContainer = ({ item }: ItemBoxProps) => {
  const itemCountdownQuery = useFetchLiveItemCountdown(item.uuid);

  return <ItemBox item={item} itemCount={itemCountdownQuery.data} />;
};

export const ItemBox = ({
  item,
  itemCount,
}: ItemBoxProps & { itemCount?: ItemCount }) => {
  const soldOut = itemCount && itemCount.remainingCount <= 0;
  return (
    <div
      className={classNames(
        "unstyled flex items-center justify-start space-x-16 rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style",
        {
          grayscale: soldOut,
        },
      )}
    >
      {item.heroImageThumbnailUrl && (
        <div className="h-[80px]">
          <div className="aspect-h-1 aspect-w-1 w-[80px] flex-none">
            <img
              src={item.heroImageThumbnailUrl}
              className="rounded-venue-brand object-cover"
            />
          </div>
        </div>
      )}
      <div className="flex-1 items-center">
        <div className="flex h-[80px] flex-col justify-center overflow-hidden">
          <div className="flex items-baseline justify-start space-x-8 text-base font-bold">
            <div>{item.name}</div>
            <div className="flex-none">
              <ItemCountdown count={itemCount?.remainingCount} />
            </div>
          </div>
          <div className="line-clamp-3 overflow-hidden text-ellipsis text-xs font-normal">
            {item.detail}
          </div>
        </div>
      </div>
      <div className="flex flex-none flex-col justify-end text-base font-medium md:font-bold">
        <Price value={item.salesPrice} />
      </div>
    </div>
  );
};

const Section = () => {
  const menu = useFetchMenu();
  const { sectionUuid } = useParams();
  const { t } = useTranslation("onsite");
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const section = menu.sections.find((s) => s.uuid == sectionUuid);
  if (!section) {
    return <div>No Section </div>;
  }
  return (
    <div className="space-y-32">
      <div className="sticky top-0 z-10 md:hidden">
        <TitleBar>
          <div className="text-3xl font-bold">{section.name}</div>{" "}
        </TitleBar>
      </div>
      <div className="text-center">{section.sectionBlurb}</div>
      <div className="space-y-24">
        <div className="grid grid-cols-1 gap-x-48 gap-y-12 xl:grid-cols-2">
          {section.items.map((item, index) => (
            <Link key={item.uuid} to={`${item.uuid}/new`} className="unstyled">
              <ItemBoxContainer item={item} key={`${index}-${item.uuid}`} />
            </Link>
          ))}
        </div>
        <div className="text-center">{section.footer}</div>
      </div>
      <PrimaryAction>
        <LinkButton primary to="/your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </div>
  );
};

export const SearchResults = ({
  menu,
  searchTerm,
  resetSearch,
}: {
  menu: Menu;
  searchTerm: string;
  resetSearch: () => void;
}) => {
  const items = getItemsForSearchTerm(menu, searchTerm);
  const { t } = useTranslation("onsite");
  return (
    <div className="space-y-32">
      <div className="sticky top-0 z-10 text-center text-2xl font-bold">
        {t("mainPage.searchResults")}
      </div>
      {items.length == 0 ? (
        <div className="space-y-24">
          <div className="text-center font-semibold">
            {t("mainPage.noSearchResults")}
          </div>
        </div>
      ) : (
        <div className="space-y-24">
          <div className="grid grid-cols-1 gap-x-48 gap-y-12 xl:grid-cols-2">
            {items.map((item, index) => (
              <Link
                key={item.uuid}
                to={`/${item.sectionUuid}/${item.uuid}/new`}
                onClick={resetSearch}
                className="unstyled"
              >
                <ItemBoxContainer item={item} key={`${index}-${item.uuid}`} />
              </Link>
            ))}
          </div>
        </div>
      )}
      <Button secondary onClick={resetSearch}>
        {" "}
        {t("mainPage.resetSearch")}
      </Button>
      <PrimaryAction>
        <LinkButton primary to="/your-order/pending">
          {t("publicMenus.yourOrder")}
        </LinkButton>
      </PrimaryAction>
    </div>
  );
};

export default Section;
